import { useEffect, useRef, type ReactNode } from 'react'
import { X } from 'lucide-react'
import { useTranslation } from 'react-i18next'

interface PortalModalProps {
  open: boolean
  onClose: () => void
  title: string
  subtitle?: string
  children: ReactNode
  footer?: ReactNode
  /** Extra header content under the title, e.g. a step indicator. */
  meta?: ReactNode
}

function focusable(root: HTMLElement): HTMLElement[] {
  return [...root.querySelectorAll<HTMLElement>('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')].filter(
    (el) => !el.hasAttribute('disabled'),
  )
}

export function PortalModal({ open, onClose, title, subtitle, children, footer, meta }: PortalModalProps) {
  const { t } = useTranslation()
  const dialogRef = useRef<HTMLDivElement>(null)
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose

  useEffect(() => {
    if (!open) return
    const previous = document.activeElement as HTMLElement | null
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const root = dialogRef.current
    focusable(root ?? document.body)[0]?.focus()
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        onCloseRef.current()
        return
      }
      if (event.key !== 'Tab' || !root) return
      const nodes = focusable(root)
      if (nodes.length === 0) return
      const first = nodes[0]
      const last = nodes[nodes.length - 1]
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }
    document.addEventListener('keydown', onKey, true)
    return () => {
      document.body.style.overflow = prevOverflow
      document.removeEventListener('keydown', onKey, true)
      previous?.focus()
    }
  }, [open])

  if (!open) return null

  return (
    <div className="prod-modal-overlay" onClick={onClose} role="presentation">
      <div
        ref={dialogRef}
        className="prod-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="portal-modal-title"
      >
        <div className="prod-modal__header">
          <div className="prod-modal__heading">
            <h2 id="portal-modal-title" className="prod-modal__title">
              {title}
            </h2>
            {meta}
          </div>
          <button type="button" className="prod-modal__close" onClick={onClose} aria-label={t('common.close')}>
            <X className="size-5" />
          </button>
        </div>
        <div className="prod-modal__body">
          {subtitle ? <p className="prod-modal__subtitle">{subtitle}</p> : null}
          {children}
        </div>
        {footer ? <div className="prod-modal__footer">{footer}</div> : null}
      </div>
    </div>
  )
}
